"use client"

import React, { useEffect, useRef } from 'react'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'
import { X } from 'react-feather'

interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  position?: 'bottom' | 'right'
  heightPercentage?: number
  showCloseButton?: boolean
  title?: string
  className?: string
}

const Drawer = ({ 
  isOpen, 
  onClose, 
  children, 
  position = 'bottom',
  heightPercentage = 80,
  showCloseButton = true,
  title,
  className = ''
}: DrawerProps) => {
  const drawerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleDragEnd = (event: any, info: PanInfo) => {
    if (position === 'bottom') {
      if (info.offset.y > 100 || info.velocity.y > 500) {
        onClose()
      }
    } else if (position === 'right') {
      if (info.offset.x > 100 || info.velocity.x > 500) {
        onClose()
      }
    }
  }

  const getDrawerStyles = () => {
    if (position === 'bottom') {
      return {
        bottom: 0,
        left: 0,
        right: 0,
        height: `${heightPercentage}vh`,
        borderTopLeftRadius: '1.5rem',
        borderTopRightRadius: '1.5rem',
      }
    } else {
      return {
        top: 0,
        right: 0,
        bottom: 0,
        width: '90%',
        maxWidth: '400px',
        borderTopLeftRadius: '1.5rem',
        borderBottomLeftRadius: '1.5rem',
      }
    }
  }

  const getAnimationVariants = () => {
    if (position === 'bottom') {
      return {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' }
      }
    } else {
      return {
        initial: { x: '100%' },
        animate: { x: 0 },
        exit: { x: '100%' }
      }
    }
  }

  const getDragConstraints = () => {
    if (position === 'bottom') {
      return { top: 0, bottom: 0 }
    } else {
      return { left: 0, right: 0 }
    }
  }

  const getDragDirection = () => {
    return position === 'bottom' ? 'y' : 'x'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          <motion.div
            ref={drawerRef}
            {...getAnimationVariants()}
            transition={{ 
              type: 'spring', 
              damping: 30, 
              stiffness: 300 
            }}
            drag={getDragDirection()}
            dragConstraints={getDragConstraints()}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            className={`fixed bg-black border-gray-800 shadow-2xl z-50 overflow-hidden ${className}`}
            style={{
              ...getDrawerStyles(),
              border: '1px solid rgb(31, 41, 55)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full h-full flex flex-col">
              {position === 'bottom' && (
                <div className="flex items-center justify-center py-2 shrink-0">
                  <div className="w-12 h-1.5 bg-gray-700 rounded-full" />
                </div>
              )}

              {(title || showCloseButton) && (
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
                  {title && (
                    <h2 className="text-xs font-semibold text-gray-100">{title}</h2>
                  )}
                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors ml-auto"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              )}

              <div className="flex-1 overflow-hidden">
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default Drawer